// The acting session on the client: loaded from GET /auth/session, flipped to `locked` or `anonymous` by any 401 the API
// client sees (UC-IDENTITY-003 E2, UC-IDENTITY-002 E1). The lock screen shows only the user's name and Control Id, kept in
// sessionStorage so a reload while locked still knows whom to show (the session view itself is refused while locked).
// Every move to `locked` or `anonymous` discards what the device had cached for reading — the service worker's API cache
// and the in-memory query cache — not only sign-out (BR-IDENTITY-005; adversarial review 16 Sep 2026, H2).
// A lock or sign-out the server did not receive — the device was offline — is remembered on the device and sent before the
// session is ever read again: until the server has it, a reload, another tab or the connection returning shows the lock screen
// or sign-in, never the session it was meant to end (UC-IDENTITY-002 A2, UC-IDENTITY-003 A5; review of 2 Oct 2026, W4).
import type { PermissionCode, SessionView } from "@visionx/contracts";
import { useSyncExternalStore } from "react";
import { onAuthProblem, purgeCaches, setActiveBranchId } from "../api/client";
import { queryClient } from "../api/query-client";
import { auth } from "../api/endpoints";
import { isApiError } from "../api/problem";
import { initialsOf, setTimezone } from "../lib/format";

/** What the lock screen may show and needs: name, role, Control Id, branch, and the tenant's PIN lengths (the session view is refused while locked). */
export interface LockedWho { displayName: string; role: string; controlIdDisplay: string; initials: string; branchName: string | null; pinMinLength?: number | undefined; pinMaxLength?: number | undefined }

export type SessionState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "locked"; who: LockedWho | null }
  | { status: "pin_setup"; session: SessionView }
  | { status: "active"; session: SessionView };

const WHO_KEY = "vx.who";
/** The lock or sign-out the server has not yet received, in localStorage so every tab of the device and a reload see it. */
const PENDING_KEY = "vx.pending";
interface Pending { kind: "lock" | "logout"; who: LockedWho | null }
let state: SessionState = { status: "loading" };
const listeners = new Set<() => void>();

function emit(next: SessionState): void {
  state = next;
  for (const l of listeners) l();
}

function readWho(): LockedWho | null {
  try { const raw = sessionStorage.getItem(WHO_KEY); return raw ? (JSON.parse(raw) as LockedWho) : null; } catch { return null; }
}
function writeWho(who: LockedWho | null): void {
  try { if (who) sessionStorage.setItem(WHO_KEY, JSON.stringify(who)); else sessionStorage.removeItem(WHO_KEY); } catch { /* storage unavailable */ }
}

function readPending(): Pending | null {
  try { const raw = localStorage.getItem(PENDING_KEY); return raw ? (JSON.parse(raw) as Pending) : null; } catch { return null; }
}
function writePending(p: Pending | null): void {
  try { if (p) localStorage.setItem(PENDING_KEY, JSON.stringify(p)); else localStorage.removeItem(PENDING_KEY); } catch { /* storage unavailable */ }
}
/** Whether the server received the call: any answer below 500 — a 401 too, for a session already locked or ended. */
const received = (err: unknown): boolean => isApiError(err) && err.status < 500;

/** Sends the remembered lock or sign-out; true once the server has it, and the record is then dropped. */
async function sendPending(p: Pending): Promise<boolean> {
  try {
    if (p.kind === "lock") await auth.lock();
    else await auth.logout();
  } catch (err) {
    if (!received(err)) return false;
  }
  if (readPending()?.kind === p.kind) writePending(null);
  return true;
}

export function whoOf(s: SessionView): LockedWho {
  const branch = s.switchableBranches.find((b) => b.id === s.activeBranchId) ?? s.assignments.find((a) => a.branchId === s.activeBranchId);
  return {
    displayName: s.user.displayName, role: s.user.role, controlIdDisplay: s.user.controlIdDisplay,
    initials: initialsOf(s.user.firstName, s.user.lastName),
    branchName: branch ? ("name" in branch ? branch.name : branch.branchName) : null,
    pinMinLength: s.settings.pinMinLength,
    pinMaxLength: s.settings.pinMaxLength,
  };
}

export function applySession(s: SessionView): void {
  setActiveBranchId(s.activeBranchId);
  setTimezone(s.settings.timezone);
  writeWho(whoOf(s));
  emit(s.isLocked ? { status: "locked", who: whoOf(s) } : s.pinSetupRequired ? { status: "pin_setup", session: s } : { status: "active", session: s });
}

/**
 * Reads the session view from the server. A remembered lock or sign-out is sent first, and while it has not reached the server
 * the device shows the lock screen or sign-in, not the session. `credentialAccepted`: the user has just signed in or unlocked,
 * which supersedes anything remembered for the session before.
 */
export async function loadSession(o: { credentialAccepted?: boolean } = {}): Promise<SessionState> {
  if (o.credentialAccepted) writePending(null);
  const pending = readPending();
  if (pending) {
    if (pending.kind === "lock" && pending.who) writeWho(pending.who);
    const sent = await sendPending(pending);
    if (pending.kind === "logout") {
      writeWho(null);
      setActiveBranchId(null);
      if (state.status !== "anonymous") emit({ status: "anonymous" });
      return state;
    }
    if (!sent) {
      if (state.status !== "locked") emit({ status: "locked", who: pending.who ?? readWho() });
      return state;
    }
  }
  try {
    const { data } = await auth.session();
    applySession(data);
  } catch (err) {
    if (isApiError(err) && err.code === "AUTHENTICATION_REQUIRED" && err.meta.reason === "locked") emit({ status: "locked", who: readWho() });
    else if (isApiError(err) && err.status === 401) { setActiveBranchId(null); emit({ status: "anonymous" }); }
    else if (state.status === "loading") {
      // Offline at first load, or a server that cannot answer (a 5xx, a 429): the session's state is unknown and the session view
      // is never cached (H2), so the device knows only whom it last showed. A user who was signed in gets the lock screen, whose
      // unlock reads the session again; a device signed out since gets sign-in. Only a 401 says the session has ended: read as
      // signed out, a server error ended patient mode on a kiosk whose session the server still held (review, 5 Oct 2026).
      const who = readWho();
      emit(who ? { status: "locked", who } : { status: "anonymous" });
    }
  }
  return state;
}

/** Nothing fetched before a lock or a session end may be shown after it: the service worker's API cache and the in-memory query cache both go (BR-IDENTITY-005; H2). */
export async function discardLocalReads(): Promise<void> {
  queryClient.clear();
  await purgeCaches();
}

export async function signOut(): Promise<void> {
  // Sign-out succeeds from the user's point of view (UC-IDENTITY-002 A1); one the server did not receive is remembered and sent
  // before the session could be read again (A2).
  try { await auth.logout(); writePending(null); } catch (err) { writePending(received(err) ? null : { kind: "logout", who: null }); }
  await discardLocalReads();
  writeWho(null);
  setActiveBranchId(null);
  emit({ status: "anonymous" });
}

export async function lockScreen(): Promise<void> {
  const who = state.status === "active" || state.status === "pin_setup" ? whoOf(state.session) : readWho();
  // A lock the server did not receive still shows the lock screen, and is remembered and sent before the session could be read
  // again (UC-IDENTITY-003 A5) — the server's own idle lock is no substitute: until it runs, a reload re-adopted the session.
  try { await auth.lock(); } catch (err) { if (!received(err) && readPending()?.kind !== "logout") writePending({ kind: "lock", who }); }
  // The call's own 401 may already have moved the store: locked by the server (idle), or ended (absolute expiry, a revoke) — then sign-in is right and a lock screen on top is not.
  if (state.status === "locked" || state.status === "anonymous") return;
  await discardLocalReads();
  emit({ status: "locked", who });
}

export function markLocked(): void {
  // The server says the session is locked: a remembered lock has nothing left to do.
  if (readPending()?.kind === "lock") writePending(null);
  if (state.status === "locked") return;
  void discardLocalReads();
  emit({ status: "locked", who: state.status === "active" || state.status === "pin_setup" ? whoOf(state.session) : readWho() });
}

export function markAnonymous(): void {
  // The session has ended at the server: nothing remembered for it is still owed.
  writePending(null);
  // While the page is still loading nobody has been shown anything under a session — the shell waits for the session read —
  // so the in-memory cache holds only what a public page (an invitation, a password reset) is reading now. Clearing it then
  // cancelled that read in flight and left the invitation on "Checking the invitation…" for ever (testing of 12 Oct 2026,
  // finding 38; UC-IDENTITY-011 step 1). The service worker's cache, which may hold an earlier session's reads, still goes.
  if (state.status === "loading") void purgeCaches();
  else void discardLocalReads();
  writeWho(null);
  setActiveBranchId(null);
  emit({ status: "anonymous" });
}

/** Test seam and the branch switcher: replace the session view in place. */
export function setSessionState(next: SessionState): void { emit(next); }
export function getSessionState(): SessionState { return state; }

export function useSession(): SessionState {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; }, () => state, () => state);
}

/** The session view when signed in, else null. */
export function useSessionView(): SessionView | null {
  const s = useSession();
  return s.status === "active" || s.status === "pin_setup" ? s.session : null;
}

export const can = (s: SessionView | null, code: PermissionCode): boolean => s !== null && (s.permissions as string[]).includes(code);

let subscribed = false;
/** Wire the 401 broadcast once per page. */
export function subscribeAuthProblems(): void {
  if (subscribed) return;
  subscribed = true;
  onAuthProblem((p) => {
    if (p.code !== "AUTHENTICATION_REQUIRED") return;
    if (p.meta.reason === "locked") markLocked();
    else if (p.meta.reason === "pin_setup") { /* the session view still answers; the router shows PIN set-up */ }
    else if (state.status !== "anonymous") markAnonymous();
  });
  // The connection is back: a remembered lock or sign-out goes now (UC-IDENTITY-002 A2, UC-IDENTITY-003 A5).
  window.addEventListener("online", () => { const p = readPending(); if (p) void sendPending(p); });
  // Another tab of this device locked or signed out without reaching the server: this tab follows at once.
  window.addEventListener("storage", (e) => {
    if (e.key !== PENDING_KEY || !e.newValue) return;
    const p = readPending();
    if (p?.kind === "lock" && (state.status === "active" || state.status === "pin_setup")) { void discardLocalReads(); emit({ status: "locked", who: p.who ?? whoOf(state.session) }); }
    else if (p?.kind === "logout" && state.status !== "anonymous") { void discardLocalReads(); writeWho(null); setActiveBranchId(null); emit({ status: "anonymous" }); }
  });
}

/** Held for the whole organisation — not only within the branches a Branch Owner owns (D39, D40). */
export function canEverywhere(s: SessionView | null | undefined, code: Parameters<typeof can>[1]): boolean {
  return can(s as never, code) && !(s?.branchScopedPermissions ?? []).some((p) => p.code === code);
}
